import React from 'react';
import { Trophy, Table2 } from 'lucide-react';
import ChartCard from './ChartCard';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { STRATEGY_COLORS } from './BatteryThermalCharts';

const Stat = ({ label, value, sub, testId, warn }) => (
  <div className="min-w-0">
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className={`font-mono text-lg font-bold break-words ${warn ? 'text-amber-600 dark:text-amber-400' : 'text-autoxium-blue'}`} data-testid={testId}>{value}</p>
    {sub && <p className="font-mono text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const minText = (s) => `${(s / 60).toFixed(1)} min`;

/** Results Summary — every value read directly off BatteryThermalResponse.summary. */
export const BatteryThermalSummaryPanel = ({ summary, limits, limiterShares, profile }) => {
  if (!summary) return null;
  const s = summary;
  const drives = profile !== 'charge';
  const charges = profile !== 'drive';
  return (
    <ChartCard icon={Trophy} title="Results Summary" testId="thermal-results-summary">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Peak Cell Core Temp." value={`${s.peak_core_temp_c.toFixed(1)} °C`}
          sub={`pack sensor peak ${s.peak_pack_temp_c.toFixed(1)} °C`} warn={s.peak_core_temp_c > limits.soft_limit_c} testId="summary-peak-core" />
        <Stat label="Final Temperature" value={`${s.final_pack_temp_c.toFixed(1)} °C`}
          sub={`core ${s.final_core_temp_c.toFixed(1)} · ${limits.coolant_label.toLowerCase()} ${s.final_coolant_temp_c.toFixed(1)} °C`} testId="summary-final-temp" />
        <Stat label={`Time Above ${limits.soft_limit_c} °C`} value={minText(s.time_above_soft_s)}
          sub={`above ${limits.hard_limit_c} °C: ${minText(s.time_above_hard_s)}`} warn={s.time_above_soft_s > 0} testId="summary-time-above" />
        <Stat label="Max Core–Pack ΔT" value={`${s.max_core_surface_dt_k.toFixed(1)} K`} sub="cell interior vs sensor" testId="summary-core-dt" />
        <Stat label="Energy Lost to Heat" value={`${s.heat_generated_kwh.toFixed(2)} kWh`}
          sub={`I²R ${s.joule_heat_kwh.toFixed(2)} · reversible ${s.reversible_heat_kwh >= 0 ? '+' : ''}${s.reversible_heat_kwh.toFixed(3)}`} testId="summary-heat" />
        <Stat label="Cooling / Heating Energy" value={`${s.cooling_energy_kwh.toFixed(2)} kWh`}
          sub={`heater ${s.heating_energy_kwh.toFixed(2)} kWh (electrical)`} testId="summary-thermal-energy" />
        {drives && (
          <Stat label="Unmet Traction Energy" value={`${s.unmet_energy_kwh.toFixed(2)} kWh`}
            sub={`${s.unmet_pct.toFixed(1)}% of ${s.requested_energy_kwh.toFixed(1)} kWh · ${minText(s.unmet_time_s)}`}
            warn={s.unmet_energy_kwh > 0.01} testId="summary-unmet" />
        )}
        {drives && (
          <Stat label="Drive" value={`${s.drive_distance_km.toFixed(1)} km`}
            sub={`${minText(s.drive_time_s)} · SoC → ${s.drive_end_soc_pct?.toFixed(1)}%`} testId="summary-drive" />
        )}
        {charges && s.charge_time_min != null && (
          <Stat label="Charge Time" value={`${s.charge_time_min.toFixed(1)} min`}
            sub={`${s.charge_start_soc_pct?.toFixed(1)} → ${s.charge_end_soc_pct?.toFixed(1)}% SoC${s.charge_target_reached ? '' : ' (target not reached)'}`}
            warn={!s.charge_target_reached} testId="summary-charge-time" />
        )}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Pack DCIR at 25 °C / 50% SoC from the measured power map, scaled to this pack:{' '}
        {s.dcir_25c_mohm.toFixed(1)} mΩ (×1.8 effective at ≤ 1C, fading to ×1.0 at 2C — see the guide).
        Thermal-network energy balance closes to {s.energy_balance_max_err_j < 0.01 ? '< 0.01' : s.energy_balance_max_err_j.toFixed(3)} J per step.
      </p>

      {limiterShares?.length > 0 && (
        <div className="mt-6">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Time per binding limit</p>
          <Table data-testid="thermal-limiter-table">
            <TableHeader>
              <TableRow>
                <TableHead>Limit</TableHead>
                <TableHead className="text-right">Time (min)</TableHead>
                <TableHead className="text-right">Share of run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {limiterShares.map((row) => (
                <TableRow key={row.label}>
                  <TableCell className="font-mono text-xs">{row.label}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{(row.time_s / 60).toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{row.pct_of_time.toFixed(1)} %</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </ChartCard>
  );
};

/** Strategy comparison table — rows straight from BatteryThermalResponse.comparison.rows. */
export const StrategyComparisonTable = ({ comparison, limits, profile }) => {
  if (!comparison?.rows?.length) return null;
  const drives = profile !== 'charge';
  const charges = profile !== 'drive';
  return (
    <ChartCard icon={Table2} title="Strategy Comparison Table" testId="thermal-comparison-table"
      description="Every cooling strategy run on the same vehicle, pack and profile. The highlighted row is your selection (with your edited values).">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Strategy</TableHead>
            <TableHead className="text-right">Peak core (°C)</TableHead>
            <TableHead className="text-right">Final pack (°C)</TableHead>
            <TableHead className="text-right">&gt; {limits.soft_limit_c} °C (min)</TableHead>
            {drives && <TableHead className="text-right">Unmet (kWh)</TableHead>}
            {charges && <TableHead className="text-right">Charge (min)</TableHead>}
            <TableHead className="text-right">Cooling (kWh)</TableHead>
            <TableHead className="text-right">Heating (kWh)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comparison.rows.map((r) => (
            <TableRow key={r.strategy} className={r.selected ? 'bg-muted/60 font-semibold' : ''} data-testid={`thermal-comparison-row-${r.strategy}`}>
              <TableCell className="font-mono text-xs whitespace-nowrap">
                <span className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: STRATEGY_COLORS[r.strategy] }} />
                {r.label}{r.selected ? ' ★' : ''}
              </TableCell>
              <TableCell className={`text-right font-mono text-xs ${r.peak_core_temp_c > limits.soft_limit_c ? 'text-amber-600 dark:text-amber-400' : ''}`}>{r.peak_core_temp_c.toFixed(1)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{r.final_pack_temp_c.toFixed(1)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{(r.time_above_soft_s / 60).toFixed(1)}</TableCell>
              {drives && <TableCell className="text-right font-mono text-xs">{r.unmet_energy_kwh.toFixed(2)} ({r.unmet_pct.toFixed(1)}%)</TableCell>}
              {charges && <TableCell className="text-right font-mono text-xs">{r.charge_time_min == null ? '—' : r.charge_time_min.toFixed(1)}</TableCell>}
              <TableCell className="text-right font-mono text-xs">{r.cooling_energy_kwh.toFixed(2)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{r.heating_energy_kwh.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ChartCard>
  );
};
